import {
  CalendarClock,
  ChevronDown,
  ListTree,
  Play,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksIconButton } from '../../components/liks/LiksIconButton/LiksIconButton';
import { LiksTooltip } from '../../components/liks/LiksTooltip/LiksTooltip';
import {
  crawlStatusHasHistogramCounts,
  mergeCrawlJobStatusDto,
} from '../../features/knowledge/crawlJobStatusMerge';
import { buildCrawlDetailLines } from '../../features/knowledge/crawlFormatters';
import { getCrawlOverallProgressPercent } from '../../features/knowledge/crawlProgress';
import {
  CRAWLS_IN_PROGRESS_GROUP_STATUSES,
  isAbandonedCrawl,
  isTerminalCrawlStatus,
} from '../../features/knowledge/knowledgePolling';
import { useCrawlJobStatusQuery } from '../../features/knowledge/queries/useCrawlJobStatusQuery';
import { formatNumber } from '../../utils/formatUtils';
import type {
  CrawlJob,
  CrawlJobStatusDTO,
  CrawlSchedule,
  CrawlStatus,
  WithResourceId,
} from '../../types/knowledge';
import type { DirectoryUser } from '../../types/user';
import type { I18nContextValue } from '../../types/i18nContext';
import './CrawlJobCard.css';

type CrawlJobCardProps = {
  crawl: WithResourceId<CrawlJob>;
  liveStatus?: CrawlJobStatusDTO | null;
  canManageCrawls: boolean;
  schedule?: WithResourceId<CrawlSchedule>;
  locale: string;
  directoryUsersById: Map<string, DirectoryUser>;
  translate: I18nContextValue['translate'];
  formatCrawlStatus: (status: CrawlStatus) => string;
  onSelection?: () => void;
  onSchedule?: () => void;
  onRestart?: () => void;
  onRetry?: () => void;
  onResumeAbandoned?: () => void;
  onDelete?: () => void;
};

export function CrawlJobCard({
  crawl,
  liveStatus,
  canManageCrawls,
  schedule,
  locale,
  directoryUsersById,
  translate,
  formatCrawlStatus,
  onSelection,
  onSchedule,
  onRestart,
  onRetry,
  onResumeAbandoned,
  onDelete,
}: CrawlJobCardProps) {
  const initialStatus = liveStatus?.status ?? crawl.status;
  const [isExpanded, setIsExpanded] = useState(
    () => initialStatus != null && !isTerminalCrawlStatus(initialStatus),
  );

  // Terminal crawls are not SSE-streamed; one-shot status fills histograms on expand.
  const statusSnapshotQuery = useCrawlJobStatusQuery({
    crawlId: crawl.id,
    enabled: isExpanded && !crawlStatusHasHistogramCounts(liveStatus),
  });

  const effectiveLiveStatus = useMemo(() => {
    const snapshot = statusSnapshotQuery.data ?? undefined;
    if (liveStatus && snapshot) {
      return mergeCrawlJobStatusDto(snapshot, liveStatus);
    }
    return liveStatus ?? snapshot ?? null;
  }, [liveStatus, statusSnapshotQuery.data]);

  const status = effectiveLiveStatus?.status ?? crawl.status;
  const isAbandoned = isAbandonedCrawl(crawl, effectiveLiveStatus);
  const selectedUrlCount = crawl.selectedUrlCount;
  const canManageSelection = status != null && status !== 'MAPPING';
  const isAwaitingManualSelection =
    status === 'AWAITING_SELECTION' &&
    !isAbandoned &&
    schedule?.active !== true;

  const detailLines = useMemo(
    () =>
      status != null
        ? buildCrawlDetailLines(crawl, effectiveLiveStatus, {
            translate,
            locale,
            status,
            schedule,
            directoryUsersById,
          })
        : [],
    [
      crawl,
      directoryUsersById,
      effectiveLiveStatus,
      locale,
      schedule,
      status,
      translate,
    ],
  );

  const overallProgressPercent =
    status != null && CRAWLS_IN_PROGRESS_GROUP_STATUSES.has(status)
      ? getCrawlOverallProgressPercent(effectiveLiveStatus)
      : null;

  return (
    <li className="knowledge-crawl-card">
      <div
        className={[
          'knowledge-crawl-card__shell',
          isExpanded && 'knowledge-crawl-card__shell--expanded',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="knowledge-crawl-card__header">
          <div className="knowledge-crawl-card__identity">
            <span className="knowledge-crawl-card__url">{crawl.startUrl}</span>
            <span
              className={[
                'knowledge-crawl-card__status',
                'knowledge-database__status',
                status != null &&
                  `knowledge-database__status--${status.toLowerCase()}`,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {status != null
                ? formatCrawlStatus(status)
                : translate('knowledge.notAvailable')}
            </span>
            {selectedUrlCount != null ? (
              <span className="knowledge-database__status knowledge-database__status--page-count">
                {translate('knowledge.crawlPagesBadge', {
                  count: formatNumber(selectedUrlCount, locale),
                })}
              </span>
            ) : null}
            {overallProgressPercent != null ? (
              <span className="knowledge-database__status knowledge-database__status--progress-percent">
                {translate('knowledge.crawlProgressBadge', {
                  percent: String(overallProgressPercent),
                })}
              </span>
            ) : null}
            {isAbandoned ? (
              <span className="knowledge-database__status knowledge-database__status--abandoned">
                {translate('knowledge.crawlAbandonedBadge')}
              </span>
            ) : null}
          </div>

          {canManageCrawls && (
            <div className="knowledge-crawl-card__actions">
              {canManageSelection && (
                <LiksButton
                  type="button"
                  className="knowledge-database__configure-button"
                  onClick={onSelection}
                >
                  <ListTree aria-hidden />{' '}
                  {isAwaitingManualSelection
                    ? translate('knowledge.crawlConfigure')
                    : translate('knowledge.crawlChangeSelection')}
                </LiksButton>
              )}
              <LiksButton
                type="button"
                className="knowledge-database__configure-button"
                onClick={onSchedule}
              >
                <CalendarClock aria-hidden />{' '}
                {schedule
                  ? translate('knowledge.crawlScheduleEdit')
                  : translate('knowledge.crawlScheduleAdd')}
              </LiksButton>
              {status === 'COMPLETED' && !isAbandoned && (
                <LiksButton
                  type="button"
                  className="knowledge-database__configure-button"
                  onClick={onRestart}
                >
                  <RefreshCw aria-hidden />{' '}
                  {translate('knowledge.crawlRestart')}
                </LiksButton>
              )}
              {status === 'FAILED' && !isAbandoned && (
                <LiksButton
                  type="button"
                  className="knowledge-database__configure-button"
                  onClick={onRetry}
                >
                  <RefreshCw aria-hidden /> {translate('knowledge.crawlRetry')}
                </LiksButton>
              )}
              {isAbandoned && (
                <LiksButton
                  type="button"
                  className="knowledge-database__configure-button"
                  onClick={onResumeAbandoned}
                >
                  <Play aria-hidden /> {translate('knowledge.crawlResume')}
                </LiksButton>
              )}
              <LiksTooltip
                content={translate('knowledge.delete')}
                placement="top-end"
              >
                <LiksIconButton
                  className="knowledge-database__action-button knowledge-database__action-button--delete"
                  aria-label={translate('knowledge.deleteCrawl', {
                    url: crawl.startUrl ?? crawl.id,
                  })}
                  onClick={onDelete}
                >
                  <Trash2 aria-hidden />
                </LiksIconButton>
              </LiksTooltip>
            </div>
          )}
        </div>

        <button
          type="button"
          className="knowledge-crawl-card__expand-row"
          aria-expanded={isExpanded}
          aria-label={
            isExpanded
              ? translate('knowledge.crawlCardCollapse')
              : translate('knowledge.crawlCardExpand')
          }
          onClick={() => setIsExpanded((current) => !current)}
        >
          <ChevronDown
            className={[
              'knowledge-crawl-card__chevron',
              isExpanded && 'knowledge-crawl-card__chevron--expanded',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-hidden
          />
        </button>

        {isExpanded && detailLines.length > 0 && (
          <div className="knowledge-crawl-card__stats">
            {detailLines.map((line, index) => (
              <p
                key={`${crawl.id}-stat-${index}`}
                className={[
                  'knowledge-crawl-card__stat-line',
                  line.indent && 'knowledge-crawl-card__stat-line--indent',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {line.text}
              </p>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}
